import { useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router'
import { ContainerHeader, DividerCustom, layoutStore } from 'shared/ui'
import { type ActionItem } from 'shared/ui/Actions/Actions'
import { IS_APP_LIGHT } from 'shared/config'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import { useConversationMessagesContext } from 'widgets/ConversationMessages/context'
import { ConversationPriorityMark } from './ConversationPriorityMark'
import { ConversationCloseButton } from './ConversationCloseButton'

export const ConversationHeader = observer(() => {
  const { conversationStore, organizationStore } = useEntitiesContext()
  const { pathname } = useLocation()
  const ref = useRef<HTMLDivElement>(null)
  const store = useConversationMessagesContext()
  const isHipaaCompliant = organizationStore.isHipaaCompliant

  const onExportAction = () => {
    store.exportConversationModalStore.onOpenExport(+store.conversationId)
  }

  const isRCS = store.inbox instanceof Inbox && store.inbox?.isRCS

  const onToggleActivity = () => {
    store.onToggleActivity()
  }

  const onToggleHideCalls = () => {
    store.onToggleHideCalls()
  }

  const onToggleReadAction = async () => {
    await (store.isRead ? conversationStore.onUnread : conversationStore.onRead)(
      store.conversationId
    )
  }

  const onTogglePriorityAction = async () => {
    await conversationStore.changePriority(store.conversationId, !store.priority)
  }

  const actions: ActionItem[] = [
    {
      iconButtonComponent: <ConversationCloseButton />,
    },
    {
      text: store.isRead ? 'Mark as unread' : 'Mark as read',
      onAction: onToggleReadAction,
      icon: store.isRead ? 'chatUnread' : 'chatRead',
    },
    {
      text: store.priority ? 'Remove priority' : 'Make priority',
      onAction: onTogglePriorityAction,
      icon: store.priority ? 'flagFilled' : 'flagLine',
    },
    !isHipaaCompliant && {
      text: 'Export conversation',
      onAction: onExportAction,
      icon: 'export',
    },
    {
      text: store.isShowActivity ? 'Hide activity' : 'Show activity',
      onAction: onToggleActivity,
      icon: store.isShowActivity ? 'eyeOff' : 'eye',
    },
    !isRCS && {
      text: store.isHideCalls ? 'Show calls' : 'Hide calls',
      onAction: onToggleHideCalls,
      icon: 'phoneOff',
      buttonProps: {
        iconRightProps: {
          icon: 'infoCircle',
          color: 'var(--content-neutral-primary)',
        },
        iconRightTooltipProps: {
          label: 'You can find all calls in the Calls tab',
          placement: 'top',
        },
      },
    },
    IS_APP_LIGHT && {
      dropdownItemComponent: () => <DividerCustom variant={'dropdown'} />,
    },
    IS_APP_LIGHT && {
      text: 'Open in Salesmsg',
      icon: 'linkExternalLine',
      link: pathname,
    },
  ].filter(Boolean) as ActionItem[]

  return (
    <ContainerHeader
      innerRef={ref}
      title={store?.conversation?.title}
      rightTitleContent={<ConversationPriorityMark />}
      titleIconProps={
        layoutStore.isMobileView
          ? {
              icon: 'chevronLeft',
            }
          : undefined
      }
      titleIconAction={layoutStore.isMobileView ? store.onBack : undefined}
      ellipsis
      isBorderBottom
      loading={store.loadingConversation}
      maxContentWidth={720}
      paddingRight={
        contactsDetailsGlobalStore.isCollapse && !layoutStore.isMobileView ? 70 : undefined
      }
      ariaLabel='ConversationHeader_name'
      actionsProps={{
        dropdownAriaLabel: 'threeDotsConversationSelector',
        actions: store.conversation ? actions : [],
        showItems: 2,
        gap: 8,
      }}
    />
  )
})
